export interface FertilizerType {
  id: string
  name: string
  npkRatio: {
    nitrogen: number    // N percentage
    phosphorus: number  // P percentage
    potassium: number   // K percentage
  }
  applicationRate: {
    coolSeason: string
    warmSeason: string
  }
  description: string
  commonBrands: string[]
  category: 'balanced' | 'high-nitrogen' | 'starter' | 'organic' | 'specialty'
  bagSize: number // pounds per bag (e.g., 20, 32, 42, 50)
  priceRange: {
    low: number // $ per pound (budget brands)
    mid: number // $ per pound (mid-range, default)
    high: number // $ per pound (premium brands)
  }
}

export const FERTILIZER_TYPES: Record<string, FertilizerType> = {
  'balanced-10-10-10': {
    id: 'balanced-10-10-10',
    name: '10-10-10 Balanced Fertilizer',
    npkRatio: {
      nitrogen: 10,
      phosphorus: 10,
      potassium: 10
    },
    applicationRate: {
      coolSeason: '3-4 lbs per 1000 sq ft',
      warmSeason: '2-3 lbs per 1000 sq ft'
    },
    description: 'All-purpose balanced fertilizer suitable for most lawns. Provides equal amounts of nitrogen, phosphorus, and potassium.',
    commonBrands: ['Scotts Turf Builder', 'Miracle-Gro', 'Pennington'],
    category: 'balanced',
    bagSize: 50,
    priceRange: {
      low: 1.20,
      mid: 1.35,
      high: 1.50
    }
  },
  'high-nitrogen-32-0-4': {
    id: 'high-nitrogen-32-0-4',
    name: '32-0-4 High Nitrogen',
    npkRatio: {
      nitrogen: 32,
      phosphorus: 0,
      potassium: 4
    },
    applicationRate: {
      coolSeason: '2-3 lbs per 1000 sq ft',
      warmSeason: '1-2 lbs per 1000 sq ft'
    },
    description: 'High nitrogen formula for promoting lush green growth. Ideal for established lawns needing color enhancement.',
    commonBrands: ['Scotts Green Max', 'Lesco', 'Fertilome'],
    category: 'high-nitrogen',
    bagSize: 50,
    priceRange: {
      low: 1.30,
      mid: 1.50,
      high: 1.70
    }
  },
  'starter-18-24-12': {
    id: 'starter-18-24-12',
    name: '18-24-12 Starter Fertilizer',
    npkRatio: {
      nitrogen: 18,
      phosphorus: 24,
      potassium: 12
    },
    applicationRate: {
      coolSeason: '2-3 lbs per 1000 sq ft',
      warmSeason: '1-2 lbs per 1000 sq ft'
    },
    description: 'High phosphorus starter fertilizer perfect for new lawns, overseeding, and root development.',
    commonBrands: ['Scotts Starter', 'Pennington Starter', 'Jonathan Green'],
    category: 'starter',
    bagSize: 42,
    priceRange: {
      low: 1.80,
      mid: 2.00,
      high: 2.20
    }
  },
  'slow-release-15-5-10': {
    id: 'slow-release-15-5-10',
    name: '15-5-10 Slow Release',
    npkRatio: {
      nitrogen: 15,
      phosphorus: 5,
      potassium: 10
    },
    applicationRate: {
      coolSeason: '4-5 lbs per 1000 sq ft',
      warmSeason: '3-4 lbs per 1000 sq ft'
    },
    description: 'Slow-release nitrogen formula provides steady feeding over 8-12 weeks. Reduces burning risk and extends feeding.',
    commonBrands: ['Scotts Turf Builder', 'Milorganite', 'Osmocote'],
    category: 'specialty',
    bagSize: 50,
    priceRange: {
      low: 0.60,
      mid: 0.90,
      high: 1.20
    }
  },
  'organic-6-2-0': {
    id: 'organic-6-2-0',
    name: '6-2-0 Organic Fertilizer',
    npkRatio: {
      nitrogen: 6,
      phosphorus: 2,
      potassium: 0
    },
    applicationRate: {
      coolSeason: '8-10 lbs per 1000 sq ft',
      warmSeason: '6-8 lbs per 1000 sq ft'
    },
    description: 'Natural organic fertilizer made from composted materials. Safe for pets and children, improves soil health.',
    commonBrands: ['Milorganite', 'Dr. Earth', 'Espoma'],
    category: 'organic',
    bagSize: 32,
    priceRange: {
      low: 0.60,
      mid: 0.70,
      high: 0.80
    }
  },
  'potassium-boost-0-0-50': {
    id: 'potassium-boost-0-0-50',
    name: '0-0-50 Potassium Boost',
    npkRatio: {
      nitrogen: 0,
      phosphorus: 0,
      potassium: 50
    },
    applicationRate: {
      coolSeason: '1-2 lbs per 1000 sq ft',
      warmSeason: '1-2 lbs per 1000 sq ft'
    },
    description: 'High potassium formula for stress resistance and winter hardiness. Use in fall for cool-season grasses.',
    commonBrands: ['Muriate of Potash', 'Sulfate of Potash', 'Lesco'],
    category: 'specialty',
    bagSize: 50,
    priceRange: {
      low: 0.60,
      mid: 0.80,
      high: 1.00
    }
  },
  'iron-supplement-0-0-0': {
    id: 'iron-supplement-0-0-0',
    name: 'Iron Supplement (0-0-0)',
    npkRatio: {
      nitrogen: 0,
      phosphorus: 0,
      potassium: 0
    },
    applicationRate: {
      coolSeason: '2-4 oz per 1000 sq ft',
      warmSeason: '2-4 oz per 1000 sq ft'
    },
    description: 'Iron supplement for deep green color without growth stimulation. Ideal for hot weather applications.',
    commonBrands: ['Scotts Green Max', 'Ironite', 'Fertilome'],
    category: 'specialty',
    bagSize: 10,
    priceRange: {
      low: 2.00,
      mid: 2.50,
      high: 3.00
    }
  },
  'winterizer-22-0-8': {
    id: 'winterizer-22-0-8',
    name: '22-0-8 Winterizer',
    npkRatio: {
      nitrogen: 22,
      phosphorus: 0,
      potassium: 8
    },
    applicationRate: {
      coolSeason: '2-3 lbs per 1000 sq ft',
      warmSeason: 'Not recommended'
    },
    description: 'Fall fertilizer with high nitrogen and potassium for winter hardiness and spring green-up.',
    commonBrands: ['Scotts WinterGuard', 'Pennington Winterizer', 'Jonathan Green'],
    category: 'specialty',
    bagSize: 50,
    priceRange: {
      low: 1.50,
      mid: 1.75,
      high: 2.00
    }
  }
}

export const FERTILIZER_CATEGORIES = {
  'balanced': 'Balanced NPK',
  'high-nitrogen': 'High Nitrogen',
  'starter': 'Starter Fertilizer',
  'organic': 'Organic',
  'specialty': 'Specialty'
} as const
